import { Card, Fab, makeStyles, Typography } from "@material-ui/core";
import { Icon } from "@resource/atlas";
import { atlasPlus } from "@resource/atlas/icons";
import { OrganizationCustomLogisticGroup } from "generated/schemaTypes";
import DOMPurify from "isomorphic-dompurify";
import { useState } from "react";
import useAnalytics from "react-hooks/useAnalytics";

import EditCustomLogisticModal from "./EditCustomLogisticModal";

const useStyles = makeStyles({
  root: {},
  logisticContainer: {},
  logisticCardsCont: {
    marginTop: 20,
    display: "flex",
    flexWrap: "wrap",
  },
  logisticCard: {
    marginBottom: 20,
    width: 200,
    height: 300,
    padding: 20,
    cursor: "pointer",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginRight: 20,
    "&": {
      position: "relative",
    },
    "&:after": {
      content: '""',
      position: "absolute",
      height: 60,
      width: "100%",
      bottom: 0,
      left: 0,
      borderRadius: 5,
      background:
        "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 50%, #ffffff 100%)",
    },
  },
  cardHeaderText: {
    marginTop: 0,
  },
  description: {
    overflowWrap: "anywhere",
  },
  headerCont: {
    display: "flex",
  },
  text: {
    margin: 0,
    marginRight: 20,
  },
  headerText: {
    marginTop: 0,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    "button:not(:last-child)": {
      marginRight: 10,
    },
    button: {
      minWidth: 180,
    },
  },
});

type CustomLogisticsProps = {
  customLogistics: OrganizationCustomLogisticGroup[];
  disabled?: boolean;
};

function CustomLogistics({ customLogistics, disabled }: CustomLogisticsProps) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const [selectedLogisticGroup, setSelectedLogisticGroup] =
    useState<OrganizationCustomLogisticGroup>();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className={classes.logisticContainer}>
      <div className={classes.headerCont}>
        <Typography className={classes.text} variant="h3">
          <span role="img" aria-label="Pray Hands">
            ✅
          </span>{" "}
          Prep Tips
        </Typography>
        <Fab
          disabled={disabled}
          onClick={(): void => {
            if (disabled) return;
            setModalOpen(true);
            setSelectedLogisticGroup({
              __typename: "LogisticGroup",
              id: "",
              options: [],
            });
            if (analytics) {
              analytics.track("Company Page Logistic Card Created");
            }
          }}
          color="primary"
          size="small"
          aria-label="add logistic"
        >
          <Icon content={atlasPlus} className="w-6 h-6" />
        </Fab>
      </div>
      <div className={classes.logisticCardsCont}>
        {customLogistics.map((logisticGroup) => {
          const { emoji, title, description } = logisticGroup.options[0];

          const sanitizedDescription = DOMPurify.sanitize(description, {
            ALLOWED_TAGS: ["br"],
          });
          return (
            <Card
              key={logisticGroup.id}
              className={classes.logisticCard}
              style={{ cursor: disabled ? "default" : "pointer" }}
              onClick={(): void => {
                if (disabled) return;
                setSelectedLogisticGroup(logisticGroup);
                setModalOpen(true);
                if (analytics) {
                  analytics.track("Company Page Logistic Card Clicked");
                }
              }}
            >
              <Typography className={classes.cardHeaderText} variant="h2">
                {emoji}
              </Typography>
              <Typography className={classes.cardHeaderText} variant="h4">
                {title}
              </Typography>
              <Typography className={classes.description} variant="body1">
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizedDescription,
                  }}
                />
              </Typography>
            </Card>
          );
        })}
      </div>
      {selectedLogisticGroup && (
        <EditCustomLogisticModal
          open={modalOpen}
          onExited={() => {
            setSelectedLogisticGroup(undefined);
          }}
          onClose={() => {
            setModalOpen(false);
          }}
          logisticGroup={selectedLogisticGroup}
        />
      )}
    </div>
  );
}
CustomLogistics.fragments = {
  customLogistic: EditCustomLogisticModal.fragments.customLogistic,
  customLogisticGroup: EditCustomLogisticModal.fragments.customLogisticGroup,
};

export default CustomLogistics;
